/**
 * Celestia locale — en-US (parity) — board-game-client copy.
 *
 * UI is NOT shared between playground and client (CLAUDE.md). Key set
 * mirrors ko-KR.ts exactly; i18n.test.ts enforces this.
 *
 * Plural strings use ICU MessageFormat `{n, plural, ...}` with CLDR
 * categories. English uses `one`/`other`; Korean uses `other` only
 * (CLDR: Korean has a single plural category).
 */

export const enUS: Readonly<Record<string, string>> = {
  // --- Error codes (spec §8) ---
  ERR_STALE_VERSION: "Stale game state — please retry.",
  ERR_DUPLICATE_ACTION_ID: "This action was already processed.",
  ERR_PHASE_MISMATCH: "That action is not allowed in this phase.",
  ERR_ACTOR_NOT_ELIGIBLE: "It is not your turn to act.",
  ERR_MODULE_DISABLED: "That action belongs to a disabled expansion.",
  ERR_LONE_CAPTAIN_REQUIRED: "Only available as a lone captain.",
  ERR_CAPTAIN_REQUIRED: "Only the captain may do that.",
  ERR_PASSENGER_REQUIRED: "Requires a passenger aboard the aircraft.",
  ERR_NOT_ON_AIRCRAFT: "You are no longer aboard.",
  ERR_HAND_LACKS_CARD: "That card is not in your hand.",
  ERR_PAYEQ_INCOMPLETE: "Committed cards do not cover all event symbols.",
  ERR_PAYEQ_OVERFULL: "Committed more cards than required.",
  ERR_PAYEQ_MUSTPLAY: "You must play available equipment.",
  ERR_TURBO_OPTIONAL_ABUSE: "Turbo used in an invalid configuration.",
  ERR_REROLL_DICE_INDEX: "That die cannot be re-rolled.",
  ERR_NO_BLANKS_TO_REROLL: "No blank dice to re-roll.",
  ERR_EJECT_SELF: "Cannot eject yourself or the captain.",
  ERR_DECK_EMPTY: "The city treasure deck is empty.",
  ERR_TREASURE_CITY_MISMATCH: "Treasure city does not match.",
  ERR_SPYGLASS_NOT_HELD: "You do not hold a Magic Spyglass.",
  ERR_JETPACK_NO_PROGRESS: "No city reached this journey.",
  ERR_NOT_PENDING_EJECTOR: "You have no ejection right.",
  ERR_INVALID_STATE: "Game state is invalid.",
  ERR_MISSING_ACTION_ID: "Missing action id.",
  ERR_UNKNOWN_ACTION: "Unknown action.",
  ERR_DECLARE_MUST_CAN:
    "Your equipment covers the perils — you must declare Can resolve.",
  ERR_DECLARE_MUST_CANNOT:
    "The perils are uncoverable — you must declare Cannot resolve.",

  // --- Client-side / network error fallbacks (multiplayer) ---
  ERR_NO_GAME: "No game in progress.",
  ERR_INIT_FAILED: "Failed to initialise the game.",
  ERR_STATE_FETCH_FAILED: "Could not load game state.",
  ERR_SUBMIT_FAILED: "Failed to submit your action.",

  // --- Phase names (spec §5.1) ---
  phase_StartOfJourney: "Start of Journey",
  phase_AwaitSoloCaptainChoice: "Lone Captain Choice",
  phase_AwaitRoll: "Roll Dice",
  phase_AwaitCaptainClaim: "Captain's Claim",
  phase_AwaitPassengerDecision: "Passenger Decision",
  phase_AwaitEjectionWindow: "Ejection Window",
  phase_AwaitCaptainDeclaration: "Captain Declaration",
  phase_AwaitReactionWindow: "Reaction Window",
  phase_AwaitResolution: "Pay Equipment",
  phase_AwaitTreasureChoice: "Treasure Choice",
  phase_EndOfLeg: "End of Leg",
  phase_EndOfJourney: "End of Journey",

  // --- Button labels ---
  btn_start_game: "Start",
  btn_roll_dice: "Roll dice",
  btn_confirm_landing: "Land here",
  btn_stay: "Stay aboard",
  btn_leave: "Leave (bank treasure)",
  btn_declare_can: "Declare: Can resolve",
  btn_declare_cannot: "Declare: Cannot resolve",
  btn_declare_pass: "Declare PASS",
  btn_declare_fail: "Declare FAIL",
  btn_jetpack: "Jetpack",
  btn_alt_route: "Re-roll a die",
  btn_wind_gust: "Wind Gust",
  btn_spyglass: "Magic Spyglass",
  btn_pass: "Pass",
  btn_pay_equipment: "Pay equipment",
  btn_take_treasure: "Take treasure",
  btn_play_again: "New game",
  btn_back_to_lobby: "Back to lobby",
  btn_close: "Close",

  // --- UI strings ---
  ui_loading: "Loading game…",
  ui_waiting_first_move: "Waiting for the first move…",
  ui_captain: "Captain",
  ui_hand: "Your hand",
  ui_aboard: "aboard",
  ui_left: "left",
  ui_treasures: "Treasures",
  ui_city: "City",
  ui_shared_win: "Shared victory",
  ui_winner: "Winner",
  ui_back: "Back to lobby",
  ui_me: "you",

  // --- ICU plural strings (CLDR: en one/other) ---
  ui_hand_count: "Your hand: {n, plural, one {# card} other {# cards}}",
  ui_passenger_count:
    "{n, plural, one {# passenger aboard} other {# passengers aboard}}",
  ui_treasure_count: "{n, plural, one {# treasure} other {# treasures}}",
  ui_handcount_label: "{n, plural, one {# hidden card} other {# hidden cards}}",
  ui_remaining_treasure:
    "{n, plural, one {# treasure remaining} other {# treasures remaining}}",
  ui_deadline_seconds:
    "{n, plural, one {# second left} other {# seconds left}}",

  // --- Die faces (spec §12) ---
  die_fog: "Fog",
  die_lightning: "Lightning",
  die_birds: "Birds",
  die_pirate: "Pirate",
  die_blank: "Blank",
  die_fiftyFifty: "Fifty-fifty",

  // --- Card labels (from rule schema, never from images — spec §3) ---
  card_eq_compass: "Compass · cancels Fog",
  card_eq_arrester: "Lightning Arrester · cancels Lightning",
  card_eq_foghorn: "Foghorn · cancels Birds",
  card_eq_cannon: "Cannon · cancels Pirate",
  card_turbo: "Turbo · substitutes any 1 symbol",
  card_pw_ejection: "Power · Ejection",
  card_pw_jetpack: "Power · Jetpack",
  card_pw_alternativeRoute: "Power · Alternative Route",
  card_pw_windGust: "Power · Wind Gust",
  card_spyglass: "Magic Spyglass · +2 if unused",
  card_unknown: "Card",

  // --- Connection / reconnect (multiplayer-only) ---
  conn_connected: "Connected",
  conn_reconnecting: "Reconnecting…",
  conn_reconnecting_banner:
    "Connection lost — reconnecting. Your game is safe.",

  // --- Accessibility labels ---
  a11y_leave_game: "Leave the game and return to the lobby",
  a11y_phase_badge: "Current phase: {phase}",
  a11y_deadline_ring: "Time remaining: {seconds}",
  a11y_seat_carousel: "Seats. Use arrow keys to move between seats.",
  a11y_seat: "Seat {seat}: {name}",
  a11y_card: "Card: {label}",
  a11y_die: "Die {n}: {face} ({letter})",
  a11y_reaction_prompt: "Reaction window open: {phase}. Choose an action.",
  a11y_aircraft_empty: "Aircraft: empty",
  a11y_aircraft_occupied: "Aircraft: occupied",
  a11y_aircraft_crashing: "Aircraft: crashing",
  a11y_settings: "Accessibility settings",
  a11y_loading: "Loading the game, please wait",

  // --- Accessibility settings panel ---
  set_title: "Accessibility",
  set_dyslexia_font: "Readable font",
  set_text_size: "Text size",
  set_text_normal: "Normal",
  set_text_large: "Large (+25%)",
  set_text_xlarge: "Extra large (+50%)",

  // --- Display prefs bar (wireframe re-skin) ---
  a11y_settings_title: "Display options",
  a11y_toggle_language: "Language",
  a11y_toggle_dyslexia: "Dyslexia-friendly font",
  a11y_text_size: "Text size",
  a11y_text_normal: "Normal",
  a11y_text_large: "Large +25%",
  a11y_text_xlarge: "Extra-large +50%",

  // --- Seat lane (wireframe re-skin) ---
  seat_aboard: "aboard",
  seat_left: "left",
  seat_treasure_short: "T:{n}",
  a11y_seat_avatar: "{name}, {status}",
  a11y_seat_hand: "{n} cards in hand",
  a11y_seat_treasure: "Treasure: {n}",

  // --- Phase record stack (wireframe re-skin) ---
  phase_record_done: "done",
  phase_record_cur: "active",
  phase_record_future: "upcoming",
  a11y_phase_stack: "Phase progress",

  // --- Waiting (multiplayer only — replaces PassDeviceGate) ---
  waiting_acting: "{name} is acting…",

  // --- Hand FAB ---
  a11y_hand_fab: "My hand · score",
  hand_panel_title: "My hand · score",
  hand_panel_close: "Close",
  hand_panel_score_section: "My score (treasure · private)",
  hand_score_treasures: "Treasures secured",
  hand_score_total: "Total",

  // --- Hand tray (FAB card grid) ---
  a11y_hand: "Your hand",
  a11y_card_selected: "{label}, selected",

  // --- Card schema text (rendered from rule schema, never baked into art) ---
  card_equip_compass_label: "Compass",
  card_equip_compass_footer: "cancels Fog",
  card_equip_arrester_label: "Lightning Arrester",
  card_equip_arrester_footer: "cancels Lightning",
  card_equip_foghorn_label: "Foghorn",
  card_equip_foghorn_footer: "cancels Birds",
  card_equip_cannon_label: "Cannon",
  card_equip_cannon_footer: "cancels Pirate",
  card_turbo_label: "Turbo",
  card_turbo_footer: "substitutes any 1 symbol",
  card_power_ejection_label: "Ejection",
  card_power_jetpack_label: "Jetpack",
  card_power_altroute_label: "Alternative Route",
  card_power_windgust_label: "Wind Gust",
  card_power_footer: "power card",
  card_spyglass_label: "Magic Spyglass",
  card_spyglass_footer: "+2 if unused",
  card_treasure_label: "Treasure",
  card_treasure_footer: "treasure",
  card_unknown_footer: "unknown card",

  // --- System phase (W9/W10) timed transitions ---
  sysphase_EndOfLeg_processing: "Processing leg result…",
  sysphase_EndOfLeg_success_title: "Leg successful",
  sysphase_EndOfLeg_success_body:
    "Airship advances to the next city. Aboard passengers secure treasure. Captain rotates.",
  sysphase_EndOfLeg_crash_title: "Airship crashed",
  sysphase_EndOfLeg_crash_body:
    "Players still aboard receive no reward. Journey ends immediately.",
  sysphase_EndOfJourney_processing: "Preparing new adventure…",
  sysphase_EndOfJourney_title: "New adventure",
  sysphase_EndOfJourney_body:
    "All players receive +1 equipment. Traveler tokens return to airship. City resets to 1. Captain rotates. (If any player reaches 50+ points, the game ends.)",
  sysphase_next: "Proceeding to next step shortly…",
};
